// path: /App.tsx
import { Suspense, lazy } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { RouteRobotsMeta } from '@/components/seo/RouteRobotsMeta';
import { FrontendVersionWatcher } from '@/components/system/FrontendVersionWatcher';

import { Layout } from './components/layout/Layout';
import Provider from './components/layout/Provider';

const HomePageNew = lazy(() => import('./app/HomeNew/HomePageNew'));
const LoaderPreviewPage = lazy(() => import('@/app/LoaderPreview/LoaderPreviewPage'));
const AccountPricingPage = lazy(() => import('@/app/Account/AccountPricingPage'));
const CompareReportsPage = lazy(() => import('@/app/Reports/CompareReportsPage'));
const RecentReportsPage = lazy(() => import('@/app/Reports/RecentReportsPage'));
const NotFoundPage = lazy(() => import('@/app/NotFound/NotFoundPage'));

const MethodologyPage = lazy(() => import('@/features/methodology/pages/MethodologyPage'));
const LastPropertyRedirect = lazy(() => import('@/features/property-legacy/components/LastPropertyRedirect'));
const LegacyPropertyRedirect = lazy(() => import('@/features/property-legacy/components/LegacyPropertyRedirect'));

const PropertyReportPage = lazy(() => import('@/features/report/ui/pages/PropertyReportPage'));
const PropertyConclusionPage = lazy(() => import('@/features/report/ui/pages/PropertyConclusionPage'));
const ReportSavingsGuidePage = lazy(() => import('@/features/report/ui/pages/ReportSavingsGuidePage'));
const ReportsLoginPage = lazy(() => import('@/features/report/ui/pages/ReportsLoginPage'));
const ReportsAccessPage = lazy(() => import('@/features/report/ui/pages/ReportsAccessPage'));
const SharedReportRedirect = lazy(() => import('@/features/report/ui/pages/SharedReportRedirect'));

const PrivacyPolicyPage = lazy(() => import('@/features/legal/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/features/legal/pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('@/features/legal/pages/CookiePolicyPage'));
const RefundPolicyPage = lazy(() => import('@/features/legal/pages/RefundPolicyPage'));

const AdminRoutes = lazy(() => import('@/features/admin/ui/AdminRoutes'));

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f7f2eb]">
      <div className="flex flex-col items-center gap-4 text-center text-[#5f5346]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#8b5e3c] border-t-transparent" />
        <p className="text-sm font-semibold">Loading…</p>
      </div>
    </div>
  );
}

const App = () => {
  return (
    <Provider>
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouteRobotsMeta />
        <FrontendVersionWatcher />
        <Routes>
        {/* 🔐 Custom Admin (replaces Django Admin UI at /admin) */}
        <Route path="/admin/*" element={<AdminRoutes />} />

        {/* Legacy alias */}
        <Route path="/admin-panel/*" element={<Navigate to="/admin" replace />} />
        <Route path="/loader-preview" element={<LoaderPreviewPage />} />

        <Route path="/" element={<Layout />}>
          {/* 🏠 Home */}
          <Route index element={<HomePageNew />} />

          {/* ✅ NEW REPORT — головний маршрут */}
          {/*<Route path="/property-legacy/:id" element={<PropertyReportPage />} />*/}

          {/* NEW REPORT (direct) */}
          <Route path="/report/:idOrHash" element={<PropertyReportPage />} />
          <Route path="/report/:idOrHash/conclusion" element={<PropertyConclusionPage />} />
          <Route path="/report/:idOrHash/save-money" element={<ReportSavingsGuidePage />} />
          <Route path="/shared/:token" element={<SharedReportRedirect />} />
          <Route path="/reports/login" element={<ReportsLoginPage />} />
          <Route path="/reports/access/:token" element={<ReportsAccessPage />} />

          {/* METHODOLOGY */}
          <Route path="/methodology" element={<MethodologyPage />} />

          {/* 🔁 Redirect для "last" */}
          <Route path="/property/last" element={<LastPropertyRedirect />} />
          <Route path="/reports" element={<RecentReportsPage />} />
          <Route path="/reports/recent" element={<RecentReportsPage />} />
          <Route path="/compare" element={<CompareReportsPage />} />

          {/* 🧱 Legacy URL preserve */}
          <Route path="/legacy/property/:id" element={<LegacyPropertyRedirect />} />

          {/* 🔎 Search legacy URL */}
          <Route path="/search" element={<Navigate to="/?focus=search" replace />} />

          {/* 💲 Pricing */}
          <Route path="/account" element={<AccountPricingPage />} />
          <Route path="/pricing" element={<AccountPricingPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />

          {/* Legacy legal/about aliases to avoid soft-404 indexing */}
          <Route path="/about" element={<Navigate to="/" replace />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
          <Route path="/cookie" element={<Navigate to="/cookie-policy" replace />} />

          {/* ❌ 404 — завжди в кінці */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        </Routes>
      </Suspense>
    </Provider>
  );
};

export default App;
